import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getSystemSettings } from "@/api/services/systemSettingsService";
import { useApp } from "@/contexts/AppContext";

// Controls how health zones are rendered on a given chart instance.
// 'off'   — no zones, no threshold line (clean chart)
// 'line'  — threshold dashed line only, no coloured zones
// 'bands' — coloured zone backgrounds + threshold line
export type ChartHealthDisplay = "off" | "line" | "bands";

export interface UserPreferenceDefaults {
  // Forecast Alerts
  lowCashBalanceThreshold: number;

  // Transaction Alerts
  overdueInvoiceAlert: boolean;

  // Variance Alerts
  varianceAlertThreshold: number;

  // Notifications
  emailNotifications: boolean;
  feedNotifications: boolean;

  // Forecasting
  defaultForecastView: "7-day" | "30-day" | "90-day";
  confidenceLevel: "low" | "medium" | "high";
  includeWeekends: boolean;

  // Display
  theme: "light" | "dark";

  // Navigation — role-level default landing page.
  // Resolved in order: user's My Preferences → this role default → smart permission fallback.
  // Must be a page key the role's permissions actually grant access to.
  defaultLandingPage?: string;

  // Health zone band boundaries (used by all chart instances)
  healthyThreshold: number; // above this = Healthy (default £100K)
  cautionThreshold: number; // below healthyThreshold, above this = Caution (default £50K)
  // Note: below cautionThreshold = Critical (red zone)

  // Min Cash Threshold line — independent dashed reference line on all charts
  minCashThreshold: number; // default £50K — separate from health zone boundaries

  // Per-chart health zone display mode
  chartHealthDashboard90: ChartHealthDisplay; // Dashboard 90-day card
  chartHealthForecast90: ChartHealthDisplay; // Forecast menu — 90-day page
  chartHealthForecast12m: ChartHealthDisplay; // Forecast menu — 12-month page
  chartHealthForecast3y: ChartHealthDisplay; // Forecast menu — 3-year page
}

const DEFAULT_PREFERENCES: UserPreferenceDefaults = {
  lowCashBalanceThreshold: 50000,
  overdueInvoiceAlert: true,
  varianceAlertThreshold: 10,
  emailNotifications: true,
  feedNotifications: true,
  defaultForecastView: "90-day",
  confidenceLevel: "medium",
  includeWeekends: false,
  theme: "light",
  healthyThreshold: 100000,
  cautionThreshold: 50000,
  minCashThreshold: 50000,
  chartHealthDashboard90: "bands",
  chartHealthForecast90: "bands",
  chartHealthForecast12m: "bands",
  chartHealthForecast3y: "bands",
};

interface UserPreferenceDefaultsContextType {
  defaults: UserPreferenceDefaults;
  updateDefaults: (updates: Partial<UserPreferenceDefaults>) => void;
}

const UserPreferenceDefaultsContext = createContext<
  UserPreferenceDefaultsContextType | undefined
>(undefined);

export function UserPreferenceDefaultsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { mode } = useApp();
  const [defaults, setDefaults] =
    useState<UserPreferenceDefaults>(DEFAULT_PREFERENCES);

  const updateDefaults = useCallback(
    (updates: Partial<UserPreferenceDefaults>) => {
      setDefaults((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  useEffect(() => {
    if (mode !== "live") return;

    let cancelled = false;
    (async () => {
      try {
        const settings = await getSystemSettings();
        const chart = settings.chartDisplaySettings;
        if (!cancelled && chart) {
          setDefaults((prev) => ({
            ...prev,
            healthyThreshold: chart.healthyThreshold,
            cautionThreshold: chart.cautionThreshold,
            minCashThreshold: chart.minCashThreshold,
            chartHealthDashboard90:
              chart.chartHealthDashboard90 as ChartHealthDisplay,
            chartHealthForecast90:
              chart.chartHealthForecast90 as ChartHealthDisplay,
          }));
        }
      } catch {
        // Charts fall back to DEFAULT_PREFERENCES.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode]);

  return (
    <UserPreferenceDefaultsContext.Provider
      value={{ defaults, updateDefaults }}
    >
      {children}
    </UserPreferenceDefaultsContext.Provider>
  );
}

export function useUserPreferenceDefaults() {
  const context = useContext(UserPreferenceDefaultsContext);
  if (!context) {
    throw new Error(
      "useUserPreferenceDefaults must be used within UserPreferenceDefaultsProvider",
    );
  }
  return context;
}
